import { useCallback, useSyncExternalStore } from 'react'
import type { ThemeName } from '@/design/tokens'

const STORAGE_KEY = 'neura.theme'

/**
 * Dark ist Default und entspricht der NEURA-CI. Light ist gleichrangig
 * ausgebaut, kein Zugestaendnis.
 *
 * Bewusst wird NICHT der Systemeinstellung gefolgt: das dunkle Erscheinungsbild
 * ist die Marke, nicht eine Vorliebe. Wer Light braucht, schaltet einmal um —
 * die Wahl bleibt gespeichert.
 *
 * Der erste Wert wird bereits in index.html gesetzt, bevor React laeuft —
 * sonst blitzt beim Laden kurz das falsche Theme auf.
 *
 * WARUM EIN EXTERNER SPEICHER UND KEIN useState
 *
 * Die erste Fassung hielt das Theme in `useState` innerhalb des Hakens. Damit
 * hatte JEDER Aufruf seinen eigenen Zustand: die Kopfleiste schaltete ihren um
 * und schrieb das Attribut an <html>, aber die Buehne, die ueber denselben
 * Haken mitliest, erfuhr davon nichts und rendert weiter im alten Register.
 *
 * Sichtbar wurde das als kaputtes helles Register: Kopfleiste, Dock und Karten
 * wechselten (die haengen an CSS und damit am Attribut), waehrend der
 * 3D-Hintergrund schwarz blieb — dunkler Text auf schwarzem Grund.
 *
 * Das Attribut an <html> ist die einzige Wahrheit. `useSyncExternalStore`
 * liest sie, und ein Umschalten benachrichtigt alle Mitleser.
 */

function currentDomTheme(): ThemeName {
  return document.documentElement.dataset.theme === 'light' ? 'light' : 'dark'
}

const listeners = new Set<() => void>()

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** Fuer das Serverrendern; die App laeuft rein im Browser. */
function serverSnapshot(): ThemeName {
  return 'dark'
}

export function setTheme(next: ThemeName) {
  if (currentDomTheme() === next) return
  document.documentElement.dataset.theme = next
  try {
    localStorage.setItem(STORAGE_KEY, next)
  } catch {
    // Nicht speicherbar: die Wahl gilt dann nur fuer diese Sitzung.
  }
  for (const listener of listeners) listener()
}

export function useTheme() {
  const theme = useSyncExternalStore(subscribe, currentDomTheme, serverSnapshot)

  const toggle = useCallback(() => {
    setTheme(currentDomTheme() === 'dark' ? 'light' : 'dark')
  }, [])

  return { theme, setTheme, toggle }
}
